import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useSettings } from "@/lib/settings";
import { TID } from "@/constants/tids";

const NAV_ITEMS = [
  { to: "/", label: "Acasă", end: true, tid: TID.navHome },
  { to: "/masini", label: "Mașini", tid: TID.navCars },
  { to: "/masini-la-comanda", label: "Mașini la comandă", tid: "nav-order-cars" },
  { to: "/finantare", label: "Finanțare", tid: TID.navFinancing },
  { to: "/despre", label: "Despre noi", tid: TID.navAbout },
  { to: "/contact", label: "Contact", tid: TID.navContact },
];

export default function Header() {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const linkClass = ({ isActive }) =>
    `relative text-[13px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 py-2 ${
      isActive
        ? "text-[#D4AF37]"
        : "text-white/85 hover:text-[#D4AF37]"
    }`;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/[0.06] shadow-[0_8px_30px_rgba(0,0,0,0.35)]"
            : "bg-gradient-to-b from-[#0a0a0a]/85 via-[#0a0a0a]/40 to-transparent backdrop-blur-[2px]"
        }`}
      >
        <div className="container-wide h-20 lg:h-24 flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            data-testid={TID.navLogo}
            className="flex items-center gap-3 group"
          >
            <div className="relative h-11 w-11 lg:h-12 lg:w-12 rounded-full bg-[#0a0a0a] ring-1 ring-[#D4AF37]/40 flex items-center justify-center overflow-hidden shadow-md group-hover:ring-[#D4AF37] transition-all">
              <img
                src={settings.logo_url}
                alt="AutoRIO"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="leading-none">
              <div className="text-lg lg:text-xl font-extrabold tracking-tight text-white">
                Auto<span className="gold-text">RIO</span>
              </div>
              <div className="text-[9px] uppercase tracking-[0.28em] text-white/45 mt-1">
                Dealer Premium · Suceava
              </div>
            </div>
          </Link>

          {/* Desktop nav — RIGHT aligned */}
          <div className="hidden lg:flex items-center gap-10">
            <nav className="flex items-center gap-8">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={linkClass}
                  data-testid={item.tid}
                >
                  {({ isActive }) => (
                    <>
                      <span>{item.label}</span>
                      {isActive && (
                        <motion.span
                          layoutId="nav-underline"
                          className="absolute left-0 right-0 -bottom-1 h-[2px] bg-[#D4AF37]"
                          transition={{ type: "spring", stiffness: 380, damping: 30 }}
                        />
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </nav>

            {/* Contact pill — twin numbers, integrated segmented */}
            <div className="inline-flex items-stretch rounded-full bg-[#D4AF37] text-[#0a0a0a] shadow-[0_4px_20px_-4px_rgba(212,175,55,0.5)] overflow-hidden">
              <span className="pl-3 pr-2 self-center">
                <span className="h-7 w-7 rounded-full bg-[#0a0a0a] text-[#D4AF37] flex items-center justify-center">
                  <Phone className="h-3.5 w-3.5" />
                </span>
              </span>
              <a
                href={`tel:${settings.phone.replace(/\s/g, "")}`}
                data-testid="header-phone"
                className="inline-flex items-center px-4 py-2.5 font-bold text-sm hover:bg-[#0a0a0a]/10 transition-colors"
              >
                {settings.phone}
              </a>
              {settings.phone_secondary && (
                <>
                  <span aria-hidden="true" className="my-2 w-px bg-[#0a0a0a]/25" />
                  <a
                    href={`tel:${settings.phone_secondary.replace(/\s/g, "")}`}
                    data-testid="header-phone-alt"
                    className="inline-flex items-center px-4 pr-5 py-2.5 font-bold text-sm hover:bg-[#0a0a0a]/10 transition-colors"
                  >
                    {settings.phone_secondary}
                  </a>
                </>
              )}
            </div>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid={TID.navMobileToggle}
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden relative h-11 w-11 flex items-center justify-center rounded-full border border-white/15 hover:border-[#D4AF37]/60 hover:bg-white/5 transition-colors"
            aria-label="Meniu"
          >
            {open ? (
              <X className="h-5 w-5 text-[#D4AF37]" />
            ) : (
              <Menu className="h-5 w-5 text-white" />
            )}
          </button>
        </div>
      </header>

      {/* Mobile menu fullscreen */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 lg:hidden bg-[#0a0a0a]/98 backdrop-blur-xl pt-24 pb-10 overflow-y-auto"
          >
            <div className="container-page flex flex-col gap-1 mt-4">
              {NAV_ITEMS.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.04 }}
                >
                  <NavLink
                    to={item.to}
                    end={item.end}
                    data-testid={item.tid}
                    className={({ isActive }) =>
                      `flex items-center justify-between py-4 border-b border-white/[0.06] text-xl font-bold ${
                        isActive ? "text-[#D4AF37]" : "text-white"
                      }`
                    }
                  >
                    <span>{item.label}</span>
                    <span className="text-[#D4AF37] text-xs">→</span>
                  </NavLink>
                </motion.div>
              ))}

              <motion.a
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                href={`tel:${settings.phone.replace(/\s/g, "")}`}
                className="mt-8 inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#0a0a0a] rounded-full py-4 font-bold text-base shadow-[0_10px_40px_-10px_rgba(212,175,55,0.6)]"
              >
                <Phone className="h-5 w-5" />
                {settings.phone}
              </motion.a>
              {settings.phone_secondary && (
                <motion.a
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45 }}
                  href={`tel:${settings.phone_secondary.replace(/\s/g, "")}`}
                  data-testid="mobile-nav-phone-alt"
                  className="mt-2 inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#0a0a0a] rounded-full py-4 font-bold text-base shadow-[0_10px_40px_-10px_rgba(212,175,55,0.6)]"
                >
                  <Phone className="h-5 w-5" />
                  {settings.phone_secondary}
                </motion.a>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
